import { useForm, zodResolver } from '@mantine/form';
import { useState } from 'react';

import { TSDListItem, TSDSimulatorWriteOffPayload, TSDSimulatorWriteOffPayloadSchema } from '@e-origin/contracts';

interface UseSimulatorFormInitializerProps {
  tsd: TSDListItem;
  onHide(): void;
  onSubmit(payload?: Record<string, unknown>): Promise<void> | void;
}

const addDays = (date: Date, days: number): Date => {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + days);

  return nextDate;
};

const toIsoString = (date?: Date): string | undefined => {
  return date ? date.toISOString() : undefined;
};

const appendIfFilled = (payload: Record<string, unknown>, key: string, value?: string | null) => {
  const trimmedValue = value?.trim();

  if (trimmedValue) {
    payload[key] = trimmedValue;
  }
};

export const useSimulateTsdWriteOffFormInitializer = ({ onHide, onSubmit }: UseSimulatorFormInitializerProps) => {
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<TSDSimulatorWriteOffPayload>({
    mode: 'controlled',
    initialValues: {
      notificationStage: '03',
      startOfTemporaryStorage: addDays(new Date(), -5),
      endOfTemporaryStorage: addDays(new Date(), 85),
      status: '12',
      statusReason: '08',
      grossMassDeclared: '',
      grossMassWrittenOff: '',
      numberOfPackagesDeclared: '',
      numberOfPackagesWrittenOff: '',
    },
    validate: zodResolver(TSDSimulatorWriteOffPayloadSchema),
    validateInputOnBlur: true,
  });

  const onSubmitForm = async (values: TSDSimulatorWriteOffPayload) => {
    try {
      setSubmitting(true);

      const payload: Record<string, unknown> = {
        notificationStage: values.notificationStage,
        status: values.status,
        statusReason: values.statusReason,
        startOfTemporaryStorage: toIsoString(values.startOfTemporaryStorage),
        endOfTemporaryStorage: toIsoString(values.endOfTemporaryStorage),
      };

      appendIfFilled(payload, 'grossMassDeclared', values.grossMassDeclared);
      appendIfFilled(payload, 'grossMassWrittenOff', values.grossMassWrittenOff);
      appendIfFilled(payload, 'numberOfPackagesDeclared', values.numberOfPackagesDeclared);
      appendIfFilled(payload, 'numberOfPackagesWrittenOff', values.numberOfPackagesWrittenOff);

      await onSubmit(payload);
    } catch {
      // toast handled by mutation
    } finally {
      setSubmitting(false);
      onHide();
    }
  };

  return {
    form,
    onSubmit: onSubmitForm,
    submitting,
  };
};
